import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAYS_MONDAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYS_SUNDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getDaysForMonth(year, month, weekStartsMonday) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  
  // Get start day adjusted for week start preference
  let startDay = firstDay.getDay(); // 0 = Sunday
  if (weekStartsMonday) {
    startDay = startDay === 0 ? 6 : startDay - 1; // Convert to Monday = 0
  }
  
  const days = [];
  
  // Previous month
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = startDay - 1; i >= 0; i--) {
    const d = prevDays - i;
    const m = month === 0 ? 12 : month;
    const y = month === 0 ? year - 1 : year;
    days.push({
      num: d,
      date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isCurrent: false
    });
  }
  
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({
      num: d,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isCurrent: true
    });
  }
  
  // Next month - fill to complete rows
  const totalCells = Math.ceil(days.length / 7) * 7;
  const need = totalCells - days.length;
  for (let d = 1; d <= need; d++) {
    const m = month === 11 ? 1 : month + 2;
    const y = month === 11 ? year + 1 : year;
    days.push({
      num: d,
      date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      isCurrent: false
    });
  }
  
  return days;
}

export default function CalendarView({ entryDates, onDayClick, weekStartsMonday = true }) {
  const today = new Date();
  // Use local date, not UTC
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  
  const days = getDaysForMonth(currentYear, currentMonth, weekStartsMonday);
  const dayHeaders = weekStartsMonday ? DAYS_MONDAY : DAYS_SUNDAY;
  
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };
  
  return (
    <div className="max-w-md mx-auto" data-testid="calendar-view">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" size="icon" onClick={goToPrevMonth} data-testid="prev-month">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="text-center">
          <h2 className="text-2xl font-heading font-semibold">
            {MONTH_NAMES[currentMonth]} {currentYear}
          </h2>
          {(currentMonth !== today.getMonth() || currentYear !== today.getFullYear()) && (
            <button 
              onClick={goToToday}
              className="text-sm text-primary hover:underline mt-1"
            >
              Back to today
            </button>
          )}
        </div>
        
        <Button variant="ghost" size="icon" onClick={goToNextMonth} data-testid="next-month">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {dayHeaders.map((day) => (
          <div key={day} className="text-center text-sm text-muted-foreground font-medium py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, idx) => {
          const hasEntry = entryDates.has(day.date);
          const isToday = day.date === todayStr;
          
          return (
            <button
              key={idx}
              onClick={() => onDayClick(day.date)}
              className={`
                aspect-square flex items-center justify-center text-sm font-medium rounded-full
                transition-all duration-200 hover:scale-110 w-9 h-9 mx-auto
                ${!day.isCurrent ? 'text-muted-foreground/40' : ''}
                ${hasEntry ? 'bg-green-500 text-white hover:bg-green-600' : 'hover:bg-secondary'}
                ${isToday && !hasEntry ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}
                ${isToday && hasEntry ? 'ring-2 ring-green-700 ring-offset-1 ring-offset-background' : ''}
              `}
              data-testid={`day-${day.date}`}
            >
              {day.num}
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span>Has entry</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full ring-2 ring-primary"></div>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
}
